<script setup lang="ts">
import { NidoPosition } from '~/service/experience/world/nido/enum'
import { NidoSeat } from '~/service/experience/world/nido/parts/NidoTexturePart'
import NidoTable from '~/service/experience/world/nido/parts/NidoTable'
import { NidoSingle } from '~/service/experience/world/nido/Nido'

const props = defineProps<{
  part: NidoSeat | NidoTable
  canBeCorner: boolean
}>()

const world = useWorld()

const isCorner = computed(() => props.part.lowBackrest?.isCorner || false)

const type = ref(
  props.part instanceof NidoTable
    ? 'table'
    : isCorner.value
    ? 'corner'
    : 'straight'
)

watch(type, () => {
  props.part.removeLowBackrest()

  if (props.part instanceof NidoTable) {
    props.part.switchToSeat(type.value === 'corner')
  } else {
    if (type.value === 'straight') {
      props.part.removeLowBackrest()
    } else if (type.value === 'corner') {
      props.part.removeLowBackrest()
      props.part.addLowBackrest(true)
    } else {
      props.part.switchToTable()
    }
  }

  world.save()
})

const hasSocket = computed(() => !!props.part.socket)
const hasPillow = computed(() => !!props.part.lowBackrest?.pillow)

function toggleSocket() {
  if (hasSocket.value) {
    props.part.removeSocket()
  } else {
    props.part.addSocket(false)
  }
  world.save()
}

function togglePillow() {
  if (hasPillow.value) {
    props.part.lowBackrest?.removePillow()
  } else {
    props.part.lowBackrest?.addPillow()
  }
  world.save()
}

function toggleLowBackrest(isCorner: boolean, isInner = false) {
  if (!props.part.parent.canAddLowbackrest(props.part.position)) return

  if (props.part.lowBackrest) {
    props.part.removeLowBackrest()
  } else {
    props.part.addLowBackrest(isCorner, isInner)
    if (isCorner || isInner) {
    }
  }
  world.save()
}

function toggleHighBackrest() {
  if (props.part.lowBackrest?.highBackrest) {
    props.part.lowBackrest.removeHighBackrest()
  } else {
    if (!props.part.lowBackrest) {
      toggleLowBackrest(isCorner.value)
    }
    props.part.lowBackrest?.addHighBackrest()
  }
  world.save()
}

const lowBackrestClass = computed(() =>
  props.part.parent.canAddLowbackrest(props.part.position) &&
  !props.part.lowBackrest
    ? 'cursor-pointer hover:fill-green-400 group-hover:fill-green-400 hover:stroke-black group-hover:stroke-black'
    : 'cursor-pointer hover:fill-red-400 group-hover:fill-red-400 fill-neutral-500'
)

const highBackrestClass = computed(() =>
  !props.part.lowBackrest?.highBackrest
    ? 'cursor-pointer hover:fill-green-400 group-hover:fill-green-400 hover:stroke-black group-hover:stroke-black'
    : 'cursor-pointer hover:fill-red-400 group-hover:fill-red-400 fill-neutral-500'
)
</script>

<template>
  <div
    class="flex w-full max-w-[130px] flex-col items-center justify-end gap-2 overflow-hidden xl:max-w-full"
  >
    <UiRadio
      v-model="type"
      :options="
        props.canBeCorner
          ? [
              { id: 'corner', label: $t('nido.cornerSeat') },
              { id: 'table', label: $t('nido.table') },
              { id: 'straight', label: $t('nido.seat') },
            ]
          : [
              { id: 'table', label: $t('nido.table') },
              { id: 'straight', label: $t('nido.seat') },
            ]
      "
      :class="{ 'mb-[20px] xl:mb-0': !props.canBeCorner }"
    />
    <button class="mt-2 text-sm" @click="props.part.invert()">
      <Icon name="fa6-solid:arrows-rotate" />
      {{ $t('nido.invert') }}
    </button>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      id="Vrstva_1"
      data-name="Vrstva 1"
      viewBox="0 0 382 370"
      class="w-48"
    >
      <g v-if="type !== 'table'">
        <rect
          v-if="!isCorner"
          x="128.5"
          y="51.5"
          width="125"
          height="110"
          rx="5"
          ry="5"
          fill="#fff"
          :class="highBackrestClass"
          :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="3"
          id="highBackrest"
          @click="toggleHighBackrest()"
        />
        <rect
          v-if="!isCorner"
          x="128.5"
          y="161.5"
          width="125"
          height="97"
          rx="5"
          ry="5"
          fill="#fff"
          :stroke="part.lowBackrest ? '#000' : '#bbb'"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="3"
          id="lowBackrest"
          :class="lowBackrestClass"
          @click="toggleLowBackrest(false)"
        />
        <g
          v-if="
            (isCorner && part.position === NidoPosition.LEFT) ||
            (isCorner &&
              part.position === NidoPosition.CENTER &&
              part.parent instanceof NidoSingle &&
              !part.parent.neighborLeft &&
              part.parent.neighborRight)
          "
          id="highCornerLeftBackrest"
          class="group"
          @click="toggleHighBackrest()"
        >
          <path
            d="m248.5,51.5H115.12c-2.6,0-4.76,1.99-4.98,4.57l-8.62,100.92c-.2,2.3,1.52,4.28,3.77,4.48h.15c.07,0,.15,0,.22,0h142.84c2.76,0,5-2.25,5-5.01V56.5c0-2.76-2.24-5-5-5Z"
            fill="#fff"
            :class="highBackrestClass"
            fill-rule="evenodd"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <line
            x1="118.7"
            y1="51.5"
            x2="111.28"
            y2="161.5"
            fill="none"
            class="group-hover:stroke-black"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
        </g>
        <g
          v-if="
            (isCorner && part.position === NidoPosition.LEFT) ||
            (isCorner &&
              part.position === NidoPosition.CENTER &&
              part.parent instanceof NidoSingle &&
              !part.parent.neighborLeft &&
              part.parent.neighborRight)
          "
          id="lowCornerLeftBackrest"
          class="group"
        >
          <path
            d="m248.5,161.5H106.5c-2.76,0-5,2.24-5,5v38.33c0,22.62,8.62,53.67,17.18,53.67h4.82c2.76,0,5-2.24,5-5,0,2.76,2.24,5,5,5h115c2.76,0,5-2.24,5-5v-87c0-2.76-2.24-5-5-5Z"
            fill="#fff"
            class="fill-neutral-500"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <path
            d="m128.5,253.5v-50.2c0-22.04-11.04-41.8-18-41.8"
            fill="none"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
        </g>
        <g
          v-if="
            (isCorner && part.position === NidoPosition.RIGHT) ||
            (isCorner &&
              part.position === NidoPosition.CENTER &&
              part.parent instanceof NidoSingle &&
              part.parent.neighborLeft &&
              !part.parent.neighborRight)
          "
          id="highCornerRightBackrest"
          class="group"
          @click="toggleHighBackrest()"
        >
          <path
            d="m133.5,51.5h133.38c2.6,0,4.76,1.99,4.98,4.57l8.62,100.92c.2,2.3-1.52,4.28-3.77,4.48h-.15c-.07,0-.15,0-.22,0h-5.6s-137.24,0-137.24,0c-2.76,0-5-2.24-5-5V56.5c0-2.76,2.24-5,5-5h0Z"
            fill="#fff"
            :class="highBackrestClass"
            fill-rule="evenodd"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <line
            x1="263.3"
            y1="51.5"
            x2="270.72"
            y2="161.5"
            fill="none"
            class="group-hover:stroke-black"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
        </g>
        <g
          v-if="
            isCorner &&
            part.position === NidoPosition.CENTER &&
            part.parent instanceof NidoSingle &&
            !part.parent.neighborLeft &&
            !part.parent.neighborRight
          "
          id="highCornerBackrest"
          class="group"
          @click="toggleHighBackrest()"
        >
          <path
            d="m271.87,56.07c-.22-2.59-2.39-4.57-4.98-4.57H115.12c-2.6,0-4.76,1.99-4.98,4.57l-8.62,100.92c-.2,2.3,1.52,4.28,3.77,4.48,0,0,0,0,.01,0h.14c.07,0,.15.01.22.01h5.6s22.24-.01,22.24-.01h137.24s5.6.01,5.6.01c.08,0,.15,0,.22-.01h.14s0,0,.01,0c2.25-.2,3.97-2.19,3.77-4.48l-8.62-100.92Z"
            fill="#fff"
            :class="highBackrestClass"
            fill-rule="evenodd"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <line
            x1="118.7"
            y1="51.5"
            x2="111.28"
            y2="161.5"
            fill="none"
            class="group-hover:stroke-black"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <line
            x1="263.3"
            y1="51.5"
            x2="270.72"
            y2="161.5"
            fill="none"
            class="group-hover:stroke-black"
            :stroke="part.lowBackrest?.highBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
        </g>
        <g
          v-if="
            (isCorner && part.position === NidoPosition.RIGHT) ||
            (isCorner &&
              part.position === NidoPosition.CENTER &&
              part.parent instanceof NidoSingle &&
              part.parent.neighborLeft &&
              !part.parent.neighborRight)
          "
          id="lowCornerRightBackrest"
        >
          <path
            d="m133.5,161.5h142c2.76,0,5,2.24,5,5v38.33c0,22.62-8.62,53.67-17.18,53.67h-4.82c-2.76,0-5-2.24-5-5,0,2.76-2.24,5-5,5h-115c-2.76,0-5-2.24-5-5v-87c0-2.76,2.24-5,5-5Z"
            fill="#fff"
            class="fill-neutral-500"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <path
            d="m253.5,253.5v-50.2c0-22.04,11.04-41.8,18-41.8"
            fill="none"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
        </g>
        <g
          v-if="
            isCorner &&
            part.position === NidoPosition.CENTER &&
            part.parent instanceof NidoSingle &&
            !part.parent.neighborLeft &&
            !part.parent.neighborRight
          "
          id="lowCornerBackrest"
        >
          <path
            d="m275.5,161.5H106.5c-2.76,0-5,2.24-5,5v38.33c0,22.62,8.62,53.67,17.18,53.67h4.82c2.76,0,5-2.24,5-5,0,.69.14,1.35.39,1.95.51,1.2,1.46,2.15,2.66,2.66.6.25,1.26.39,1.95.39h115c.69,0,1.35-.14,1.95-.39,1.2-.51,2.15-1.46,2.66-2.66.25-.6.39-1.26.39-1.95,0,2.76,2.24,5,5,5h4.82c8.56,0,17.18-31.05,17.18-53.67v-38.33c0-2.76-2.24-5-5-5Z"
            fill="#fff"
            class="fill-neutral-500"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <path
            d="m128.5,253.5v-50.2c0-22.04-11.04-41.8-18-41.8"
            fill="none"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
          <path
            d="m253.5,253.5v-50.2c0-22.04,11.04-41.8,18-41.8"
            fill="none"
            fill-rule="evenodd"
            :stroke="part.lowBackrest ? '#000' : '#bbb'"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
          />
        </g>
        <path
          v-if="part.lowBackrest?.pillow"
          d="m248.5,197.5h-115c-2.76,0-5,2.24-5,5v34.33c0-2.84,1.93-5.34,4.72-6.11,6.29-1.73,12.75-2.81,19.28-3.17,25.65-1.4,51.35-1.4,77,0,6.52.36,12.99,1.43,19.28,3.17h0c2.79.77,4.72,3.27,4.72,6.11v-34.33c0-2.76-2.24-5-5-5Z"
          fill="#fff"
          class="fill-neutral-500"
          fill-rule="evenodd"
          stroke="#000"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="3"
          id="pillow"
        />
        <path
          id="seat"
          d="m248.78,230.72h0c-6.29-1.73-12.75-2.81-19.28-3.17-25.65-1.4-51.35-1.4-77,0-6.52.36-12.99,1.43-19.28,3.17-2.79.77-4.72,3.27-4.72,6.11v16.67c0,2.76,2.24,5,5,5h115c2.76,0,5-2.24,5-5v-16.67c0-2.84-1.93-5.34-4.72-6.11Z"
          fill="#fff"
          class="fill-neutral-500"
          fill-rule="evenodd"
          stroke="#000"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="3"
        />
      </g>
      <rect
        x="128.5"
        y="252.5"
        width="125"
        height="6"
        fill="#fff"
        stroke="#000"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="3"
        id="table"
        v-else
      />
      <rect
        v-if="
          part.position === NidoPosition.LEFT ||
          part.parent instanceof NidoSingle
        "
        x="132.5"
        y="258.5"
        width="6"
        height="60"
        fill="#fff"
        stroke="#000"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="3"
        id="leftFoot"
      />
      <rect
        v-if="
          part.position === NidoPosition.RIGHT ||
          part.parent instanceof NidoSingle
        "
        x="243.5"
        y="258.5"
        width="6"
        height="60"
        fill="#fff"
        stroke="#000"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="3"
        id="rightFoot"
      />
      <rect
        x="138.5"
        y="258.5"
        width="105"
        height="10"
        fill="#fff"
        stroke="#000"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="3"
      />
      <rect
        v-if="part.socket"
        x="138.5"
        y="258.5"
        width="25"
        height="16"
        fill="#fff"
        stroke="#000"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="3"
        id="socket"
      />
    </svg>
    <UiCheckbox
      @update:model-value="toggleSocket()"
      :model-value="hasSocket"
      :label="$t('nido.socket.title')"
    />
    <UiCheckbox
      @update:model-value="togglePillow()"
      :model-value="hasPillow"
      :disabled="!part.lowBackrest"
      :label="$t('nido.pillow')"
    />
  </div>
</template>
